/* eslint-disable @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { Injectable } from '@angular/core'
import { map, tap } from 'rxjs/operators'
import { Observable } from 'rxjs'

import { ApiService } from '@shared/services/api.service'
import { withFeedback } from '@app/core/interceptors/feedback-context'
import { VehicleGroup } from '@shared/models/vehicle-group.model'
import { Depot } from '@shared/models/depot.model'
import { InputVehicle, Vehicle, VehicleModelType } from '@shared/models/vehicle.model'
import { InputDepotOrVehicleGroup } from '../config/fleet/shared/config-fleet-form/config-fleet-form.component'
import { toFormData } from '@core/helpers/to-form-data'
import { Setting } from '@shared/models/setting.model'
import { NoteHistory } from '@shared/models/note.model'
import { Odometer } from '@shared/models/odometer.model'
import { PagesData } from '@shared/models/pages-data.model'
import { FilterValues } from '@shared/models/filter-values.model'
import { ExportFileTypes } from '@shared/models/export-file-types.enum'
import { EngineHours } from '@shared/models/engine-hours.model'

interface IsUniqueVin {
  isUnique: boolean
}

@Injectable({ providedIn: 'root' })
export class VehiclesService {
  vehiclesTotal: number

  constructor(private apiService: ApiService) {}

  getVehicles(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: { [param: string]: any },
    fields?: any[],
    areByFields = false,
  ): Observable<PagesData<Vehicle>> {
    const path: string = areByFields ? 'fields' : 'json'
    const toVehicle = (vehicle: Vehicle) => new Vehicle(vehicle)

    return this.apiService
      .getPageable<Vehicle>(`/vehicles/${path}`, pageIndex, pageSize, sort, filterValues, fields)
      .pipe(map(({ data, ...rest }) => ({ data: (<Vehicle[]>data).map(toVehicle), ...rest })))
  }

  getVehicleList(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Vehicle)[],
    areaFields = false,
  ): Observable<Vehicle[]> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return this.getVehicles(pageIndex, pageSize, sort, filterValues, fields, areaFields).pipe(
      tap(({ meta }) => (this.vehiclesTotal = meta.total)),
      map(({ data }) => data),
    )
  }

  getVehicle(id: string, fields?: string[]): Observable<Vehicle> {
    const params = fields && { 'fields[]': fields }
    return this.apiService.get(`/vehicles/${id}`, { params }).pipe(map((resp) => new Vehicle(<Vehicle>resp['data'])))
  }

  addVehicle(vehicle: Partial<InputVehicle>): Observable<Vehicle> {
    return this.apiService.post('/vehicles', vehicle, { context: withFeedback() }).pipe(map((resp) => new Vehicle(<Vehicle>resp['data'])))
  }

  getVehicleGroupList(
    filterValues?: { [param: string]: string | number | (string | number)[] },
    fields?: (keyof VehicleGroup)[],
    sort?: string,
  ): Observable<VehicleGroup[]> {
    return this.getVehicleGroups(0, 1000, sort, filterValues, fields).pipe(map((vehicleGroups) => vehicleGroups.data))
  }

  getDepotList(
    filterValues?: { [param: string]: string | number | (string | number)[] },
    fields?: (keyof Depot)[],
    sort?: string,
  ): Observable<Depot[]> {
    return this.getDepots(0, 1000, sort, filterValues, fields).pipe(map((depots) => depots.data))
  }

  getVehicleGroups(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: { [param: string]: string | number | (string | number)[] },
    fields?: (keyof VehicleGroup)[],
  ): Observable<{ data: VehicleGroup[]; meta: { [key: string]: any } }> {
    return this.apiService.getPageable<VehicleGroup>('/vehicle-groups', pageIndex, pageSize, sort, filterValues, fields)
  }

  getDepots(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: { [param: string]: string | number | (string | number)[] },
    fields?: (keyof Depot)[],
  ): Observable<{ data: Depot[]; meta: { [key: string]: any } }> {
    return this.apiService.getPageable<Depot>('/depots', pageIndex, pageSize, sort, filterValues, fields)
  }

  getVehicleGroup(id: string): Observable<VehicleGroup> {
    return this.apiService.get(`/vehicle-groups/${id}`).pipe(map((resp) => new VehicleGroup(resp['data'])))
  }

  getDepot(id: string): Observable<Depot> {
    return this.apiService.get(`/depots/${id}`).pipe(map((resp) => new Depot(resp['data'])))
  }

  addDepot(data: InputDepotOrVehicleGroup): Observable<Depot> {
    return this.apiService.post(`/depots`, data).pipe(map((resp) => new Depot(resp['data'])))
  }

  addVehicleGroup(data: InputDepotOrVehicleGroup): Observable<VehicleGroup> {
    return this.apiService.post(`/vehicle-groups`, data).pipe(map((resp) => new VehicleGroup(resp['data'])))
  }

  editDepot(id: string, data: InputDepotOrVehicleGroup): Observable<Depot> {
    return this.apiService.patch(`/depots/${id}`, data).pipe(map((resp) => new Depot(resp['data'])))
  }

  editVehicleGroup(id: string, data: InputDepotOrVehicleGroup): Observable<VehicleGroup> {
    return this.apiService.patch(`/vehicle-groups/${id}`, data).pipe(map((resp) => new VehicleGroup(resp['data'])))
  }

  deleteDepot(id: string, assignDepotId?: number | boolean) {
    const params = {
      ...(assignDepotId && { assignDepotId }),
    }
    return this.apiService.delete(`/depots/${id}`, { params })
  }

  deleteVehicleGroup(id: string) {
    return this.apiService.delete(`/vehicle-groups/${id}`)
  }

  editVehicle(id: number, vehicle: Partial<InputVehicle>) {
    return this.apiService.post(`/vehicles/${id}`, vehicle).pipe(map((resp) => new Vehicle(<Vehicle>resp['data'])))
  }

  uploadPicture(id: number, data: unknown): Observable<Vehicle> {
    return this.apiService.post(`/vehicles/${id}`, toFormData(data)).pipe(map((resp) => new Vehicle(<Vehicle>resp['data'])))
  }

  exportFile(columns: string[], sort: string, filterValues: FilterValues, type: ExportFileTypes): Observable<string> {
    return this.apiService.exportFile(`/vehicles/${type}`, columns, sort, filterValues)
  }

  getDefaultExcessiveIdling(): Observable<Setting> {
    return this.apiService.get('/settings/excessiveIdling').pipe(map((resp) => resp['data']))
  }

  getDefaultEcoSpeed(): Observable<Setting> {
    return this.apiService.get('/settings/ecoSpeed').pipe(map((resp) => resp['data']))
  }

  checkVehicleVin(teamId: number, vin: string, vehicleId?: number): Observable<boolean> {
    const params = {
      ...(vehicleId && { vehicleId }),
      teamId,
      vin,
    }

    return this.apiService.get('/vehicles/check-vehicle-vin', { params }).pipe(
      map((response) => response['data']),
      map((isUniqueDriverId: IsUniqueVin) => isUniqueDriverId.isUnique),
    )
  }

  deleteVehicle(id: number): Observable<void> {
    return this.apiService.delete(`/vehicles/${id}`) as any
  }

  undeleteVehicle(id: number): Observable<void> {
    return this.apiService.post(`/vehicles/${id}/undelete`, {}) as any
  }

  restoreVehicle(id: number): Observable<Vehicle> {
    return this.apiService.post(`/vehicles/${id}/restore`, {}).pipe(map((resp) => new Vehicle(<Vehicle>resp['data'])))
  }

  archiveVehicle(id: number): Observable<void> {
    return this.apiService.patch(`/vehicles/${id}/archive`, {})
  }

  getVehicleNotes(id: number, noteType: 'client' | 'admin'): Observable<NoteHistory[]> {
    return this.apiService.get(`/vehicle-notes/${id}/${noteType}`).pipe(map((resp) => resp['data'])) as Observable<NoteHistory[]>
  }

  addClientNote(id: number | string, note: string, noteType: 'admin' | 'client'): Observable<void> {
    const body = {
      [`${noteType}Note`]: note,
    }

    return this.apiService.post(`/vehicles/${id}/notes`, body)
  }

  getOdometer(vehicleId: number): Observable<Odometer> {
    return this.apiService.get(`/vehicles/${vehicleId}/odometer`).pipe(map((resp) => new Odometer(<Odometer>resp['data'])))
  }

  updateOdometer(vehicleId: number, odometer: number, occurredAt: string): Observable<Odometer> {
    return this.apiService
      .post(`/vehicles/${vehicleId}/odometer`, { odometer, occurredAt })
      .pipe(map((resp) => new Odometer(<Odometer>resp['data'])))
  }

  getOdometerHistory(vehicleId: number, pageIndex = 0, pageSize = 10): Observable<PagesData<Odometer>> {
    const toOdometer = (odometer: Odometer) => new Odometer(odometer)

    return this.apiService
      .getPageable<Odometer>(`/vehicles/${vehicleId}/odometer/history`, pageIndex, pageSize)
      .pipe(map(({ data, ...rest }) => ({ data: (<Odometer[]>data).map(toOdometer), ...rest })))
  }

  deleteOdometerHistory(vehicleId: number, odometerId: number) {
    return this.apiService.delete(`/vehicles/${vehicleId}/odometer/${odometerId}`)
  }

  getVehiclesTypes(): Observable<VehicleModelType[]> {
    return this.apiService.get(`/vehicles/types?limit=1000&sort=order`, {}).pipe(map((resp) => resp['data']))
  }

  getVehiclesTypesByHah(hash: string): Observable<VehicleModelType[]> {
    return this.apiService.get(`/tracklink/${hash}/vehicles/types?limit=1000`).pipe(map((resp) => resp['data']))
  }

  updateEngine(vehicleId: number, engineHours: number): Observable<EngineHours> {
    return this.apiService.post(`/vehicles/${vehicleId}/engine-hours`, { engineHours }).pipe(map((resp) => resp['data']))
  }

  getEngineHoursHistory(vehicleId: number, pageIndex = 0, pageSize = 10): Observable<PagesData<EngineHours>> {
    return this.apiService
      .getPageable<Odometer>(`/vehicles/${vehicleId}/engine-hours`, pageIndex, pageSize)
      .pipe(map((response) => response))
  }

  deleteEngineHoursHistory(vehicleId: number, engineHoursId: number) {
    return this.apiService.delete(`/vehicles/${vehicleId}/engine-hours/${engineHoursId}`)
  }

  getEngineHours(vehicleId: number): Observable<EngineHours> {
    return this.apiService.get(`/vehicles/${vehicleId}/engine-hours/current`)
  }

  getVehicleCount(): Observable<{ count: number }> {
    return this.apiService.get(`/vehicles/count`, { responseType: 'json' })
  }
}
